import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { money } from '@/lib/utils';
import { DataTable, FilterField, SearchFilterBar, StatTile } from '@/components/ui/layout';
import { Alert, Checkbox } from '@/components/ui/primitives';
import { Pager, formatQty, signedMoney, signedQty } from './stock-control-common';
import { VARIANCE_PAGE_SIZE, type StockTakeVariance } from './stock-control-types';

export function StockTakeVariancePanel({ takeId }: { takeId: string }) {
  const [differencesOnly, setDifferencesOnly] = useState(true);
  const [offset, setOffset] = useState(0);

  const variance = useQuery({
    queryKey: ['stock-control-take-variance', takeId, differencesOnly, offset],
    queryFn: () =>
      get<StockTakeVariance>(
        `/pharmacy/stock-takes/${takeId}/variance${qs({ differencesOnly, limit: VARIANCE_PAGE_SIZE, offset })}`,
      ),
    placeholderData: keepPreviousData,
  });

  if (variance.error) {
    return <Alert variant="warning">{describeError(variance.error)}</Alert>;
  }

  const summary = variance.data?.summary;
  const netTone = summary === undefined || summary.netMinor === 0 ? 'default' : summary.netMinor < 0 ? 'destructive' : 'success';
  const hasNext = variance.data !== undefined && offset + variance.data.lines.length < variance.data.totalLines;
  const paged = offset > 0 || hasNext;

  return (
    <div className="space-y-4">
      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label={tr('pharmacy', 'shortage')}
            value={money(summary.shortageMinor)}
            sub={`${summary.shortageLines} line${summary.shortageLines === 1 ? '' : 's'} short`}
            tone={summary.shortageMinor > 0 ? 'destructive' : 'default'}
          />
          <StatTile
            label={tr('pharmacy', 'overage')}
            value={money(summary.overageMinor)}
            sub={`${summary.overageLines} line${summary.overageLines === 1 ? '' : 's'} over`}
            tone={summary.overageMinor > 0 ? 'success' : 'default'}
          />
          <StatTile label={tr('pharmacy', 'net_at_cost')} value={signedMoney(summary.netMinor)} sub={tr('pharmacy', 'overage_minus_shortage')} tone={netTone} />
          <StatTile
            label={tr('pharmacy', 'uncounted_lines')}
            value={summary.uncountedLines}
            sub={tr('pharmacy', 'x_matched_exactly', { matchedLines: summary.matchedLines })}
            tone={summary.uncountedLines > 0 ? 'warning' : 'default'}
          />
        </div>
      )}

      <SearchFilterBar
        className="mb-0"
        filters={
          <FilterField><Checkbox
              checked={differencesOnly}
              onCheckedChange={(checked) => {
                setDifferencesOnly(checked);
                setOffset(0);
              }}
              label={tr('pharmacy', 'show_only_lines_that_differ')}
            /></FilterField>
        }
        onReset={
          differencesOnly
            ? undefined
            : () => {
                setDifferencesOnly(true);
                setOffset(0);
              }
        }
      />

      <DataTable
        dense
        rows={variance.data?.lines}
        loading={variance.isLoading} refreshing={variance.isFetching}
        rowKey={(line) => line.lineId}
        empty={
          differencesOnly
            ? tr('pharmacy', 'no_differences_so_far_every_counted_line')
            : tr('pharmacy', 'this_sheet_has_no_lines')
        }
        footer={
          paged ? (
            <div className="w-full">
              <Pager
                offset={offset}
                pageSize={VARIANCE_PAGE_SIZE}
                rowCount={variance.data?.lines.length ?? 0}
                hasNext={hasNext}
                disabled={variance.isFetching}
                onOffsetChange={setOffset}
              />
            </div>
          ) : undefined
        }
        columns={[
          { header: tr('pharmacy', 'code'), sort: (line) => line.code, cell: (line) => <span className="font-mono text-xs">{line.code}</span> },
          { header: tr('pharmacy', 'product'), sort: (line) => line.name, cell: (line) => `${line.name} ${line.strength ?? ''}`.trim() },
          { header: tr('pharmacy', 'batch'), sort: (line) => line.batchNo, cell: (line) => <span className="font-mono text-xs">{line.batchNo}</span> },
          { header: tr('pharmacy', 'system'), align: 'right', sort: (line) => line.systemQty, cell: (line) => formatQty(line.systemQty) },
          { header: tr('pharmacy', 'moved_since_snapshot'), align: 'right', sort: (line) => line.movedSinceSnapshotQty, cell: (line) => signedQty(line.movedSinceSnapshotQty) },
          { header: tr('pharmacy', 'expected'), align: 'right', sort: (line) => line.expectedQty, cell: (line) => formatQty(line.expectedQty) },
          {
            header: tr('pharmacy', 'counted_2'),
            align: 'right',
            sort: (line) => line.countedQty,
            cell: (line) =>
              line.countedQty === null ? (
                <span className="text-muted-foreground">{tr('pharmacy', 'not_counted_2')}</span>
              ) : (
                formatQty(line.countedQty)
              ),
          },
          {
            header: tr('pharmacy', 'variance'),
            align: 'right',
            sort: (line) => line.varianceQty,
            cell: (line) =>
              line.varianceQty === null ? (
                '—'
              ) : (
                <span className={line.varianceQty < 0 ? 'font-medium text-destructive' : line.varianceQty > 0 ? 'font-medium text-success' : ''}>
                  {signedQty(line.varianceQty)}
                </span>
              ),
          },
          {
            header: tr('pharmacy', 'variance_value'),
            align: 'right',
            sort: (line) => line.varianceValueMinor,
            cell: (line) => (line.varianceValueMinor === null ? '—' : signedMoney(line.varianceValueMinor)),
          },
        ]}
      />

      <p className="text-xs text-muted-foreground">
        {tr('pharmacy', 'expected_is_the_snapshot_quantity_plus_whatever')}
      </p>
    </div>
  );
}
